import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { formatBankingDateTime } from '../../utils/bankingDateTime';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { INVOICE_TO_INVOICE_PAGE_SIZE } from '../../utils/invoiceSchema';
import { paginateInvoiceList } from './invoiceMatchUtils';
import { iv, ivSurfaceSx } from './invoiceTheme';

const formatMoney = (v) =>
  Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatMovementDate = (row) => {
  const raw = row?.valueDate || row?.bookingDate || row?.date || row?.createdAt;
  if (raw == null || raw === '') return '—';
  if (typeof raw === 'string' || typeof raw === 'number') {
    const formatted = formatBankingDateTime(raw);
    return formatted || String(raw);
  }
  if (typeof raw?.toDate === 'function') {
    return formatBankingDateTime(raw.toDate()) || '—';
  }
  if (raw instanceof Date) {
    return formatBankingDateTime(raw) || '—';
  }
  if (typeof raw?.seconds === 'number') {
    return formatBankingDateTime(new Date(raw.seconds * 1000)) || '—';
  }
  return '—';
};

const movementSender = (row) =>
  String(
    row.counterpartyName || row.senderName || row.aciklama || row.description || 'Gönderen bilinmiyor'
  );

/**
 * Faturalandırılacak — eşleşmemiş banka hareketleri.
 * Taslağı olan hareketler listede gösterilmez (çift taslak olmasın diye).
 */
function InvoiceToInvoicePanel({
  unmatchedMovements = [],
  loading,
  autoMatching,
  onRunAutoMatch,
  selectedMovementId = null,
  onSelectMovement,
  onHideMovement
}) {
  const rows = unmatchedMovements || [];
  // Eskiden ilk 12 gösterilip gerisi "+57 hareket daha" yazısıyla erişilemez kalıyordu.
  const [page, setPage] = useState(1);
  const pagination = paginateInvoiceList(rows, page, INVOICE_TO_INVOICE_PAGE_SIZE);
  const preview = pagination.items;

  useEffect(() => {
    if (page > pagination.totalPages) setPage(pagination.totalPages);
  }, [page, pagination.totalPages]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: iv.text, letterSpacing: '-0.02em' }}>
            Faturalandırılacak
          </Typography>
          <Typography sx={{ fontSize: 13, color: iv.textMuted, mt: 0.3 }}>
            Banka havalelerini UKBA işlemleriyle eşleştirip taslak oluşturun. Onay için sol menü → Onay
            Bekleyenler.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={autoMatching ? <CircularProgress size={14} color="inherit" /> : <SyncAltIcon />}
          onClick={onRunAutoMatch}
          disabled={autoMatching || loading}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: iv.primary,
            boxShadow: 'none',
            '&:hover': { bgcolor: iv.primaryHover, boxShadow: 'none' }
          }}
        >
          {autoMatching ? 'Eşleştiriliyor…' : 'Yeni Eşleşmeleri Bul'}
        </Button>
      </Box>

      <Box sx={{ ...ivSurfaceSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ px: 1.75, py: 1.2, borderBottom: `1px solid ${iv.border}` }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800 }}>
            Eşleşmeyi bekleyen banka hareketleri
            {rows.length > 0 ? ` (${rows.length})` : ''}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ p: 4, display: 'grid', placeItems: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : preview.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <AccountBalanceOutlinedIcon sx={{ fontSize: 36, color: iv.textFaint, mb: 1 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: iv.text }}>
                Eşleşmemiş gelen hareket yok
              </Typography>
              <Typography sx={{ fontSize: 13, color: iv.textMuted, mt: 0.5 }}>
                Yeni havale geldiğinde burada listelenir. İsterseniz eşleştirmeyi yeniden çalıştırın.
              </Typography>
            </Box>
          ) : (
            preview.map((row) => {
              const bankLabel = String(row.bankCode || row.provider || 'Banka');
              return (
                <Box
                  key={row.id || `${bankLabel}-${row.amount}-${formatMovementDate(row)}`}
                  onClick={() => onSelectMovement?.(row)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.75,
                    py: 1.25,
                    borderBottom: `1px solid ${iv.border}`,
                    cursor: onSelectMovement ? 'pointer' : 'default',
                    bgcolor: selectedMovementId && row.id === selectedMovementId ? iv.primarySoft : 'transparent',
                    borderLeft:
                      selectedMovementId && row.id === selectedMovementId
                        ? `3px solid ${iv.primary}`
                        : '3px solid transparent',
                    '&:hover': onSelectMovement ? { bgcolor: iv.primarySoft } : {}
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.3 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: iv.text }} noWrap>
                        {movementSender(row)}
                      </Typography>
                      <Chip size="small" label={bankLabel} sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: iv.textMuted }}>
                      {formatMovementDate(row)} · Bekliyor: otomatik eşleşme
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: iv.text, whiteSpace: 'nowrap' }}>
                    {formatMoney(row.amount)} ₺
                  </Typography>
                  {/* Eski / karsiligi baska yerde olan hareketler kuyrukta
                      birikiyordu. Banka kaydi SILINMEZ, yalniz bu listeden
                      cikarilir; ayarlardan geri alinabilir. */}
                  {onHideMovement ? (
                    <Tooltip title="Faturalandırılacaklardan çıkar (banka kaydı silinmez)">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onHideMovement(row);
                        }}
                        sx={{ flexShrink: 0, color: iv.textMuted }}
                      >
                        <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Box>
              );
            })
          )}
        </Box>
        {rows.length > 0 ? (
          <Box
            sx={{
              px: 1.75,
              py: 0.9,
              borderTop: `1px solid ${iv.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexShrink: 0
            }}
          >
            <Typography sx={{ fontSize: 12, color: iv.textMuted, fontWeight: 600 }}>
              {pagination.from}–{pagination.to} / {pagination.total} hareket
            </Typography>
            {pagination.totalPages > 1 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  disabled={pagination.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Önceki sayfa"
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: iv.text, minWidth: 54, textAlign: 'center' }}>
                  {pagination.page} / {pagination.totalPages}
                </Typography>
                <IconButton
                  size="small"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  aria-label="Sonraki sayfa"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default InvoiceToInvoicePanel;
